// Authentication context for React app
import React, { createContext, useContext, useState, useEffect } from 'react';
import { isTokenExpired, getTokenExpirationTime, handleAuthError } from '../utils/authUtils';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('mipa_token'));
    const [loading, setLoading] = useState(true);

    // API base URL
    // API URL configuration - use localhost for development
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003';

    // Monitor token expiration
    useEffect(() => {
        if (!token) return;

        // Don't check on mount - let backend validate on API calls
        // This prevents false positives
        
        // Set up interval to check token expiration every minute
        const interval = setInterval(() => {
            const currentToken = localStorage.getItem('mipa_token');
            if (!currentToken) return;
            
            // Only show warning before expiration, don't auto-logout
            const timeRemaining = getTokenExpirationTime(currentToken);
            if (timeRemaining < 5 * 60 * 1000 && timeRemaining > 4 * 60 * 1000) {
                if (window.showToast) {
                    window.showToast('Your session will expire in 5 minutes. Please save your work.', 'warning', 10000);
                }
            }
        }, 60000); // Check every minute

        return () => clearInterval(interval);
    }, [token]);

    useEffect(() => {
        // Check if user is already logged in
        const savedToken = localStorage.getItem('mipa_token');
        const savedUser = localStorage.getItem('mipa_user');
        
        if (savedToken && savedUser) {
            try {
                setToken(savedToken);
                setUser(JSON.parse(savedUser));
            } catch (error) {
                console.error('Failed to parse saved user data:', error);
                // Clear invalid data
                localStorage.removeItem('mipa_token');
                localStorage.removeItem('mipa_user');
                setToken(null);
                setUser(null);
            }
        }
        setLoading(false);
    }, []);

    const login = async (username, password) => {
        try {
            const response = await fetch(`${API_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password }),
            });

            if (!response.ok) {
                let errorMessage = 'Login failed';
                try {
                    const error = await response.json();
                    errorMessage = error.detail || errorMessage;
                } catch (e) {
                    // If response is not JSON, use status text
                    errorMessage = response.statusText || errorMessage;
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();
            
            // Validate response data
            if (!data.access_token || !data.user_info) {
                throw new Error('Invalid response from server');
            }
            
            // Store token and user info
            localStorage.setItem('mipa_token', data.access_token);
            localStorage.setItem('mipa_user', JSON.stringify(data.user_info));
            
            setToken(data.access_token);
            setUser(data.user_info);
            
            return { success: true };
        } catch (error) {
            console.error('Login error:', error);
            return { success: false, error: error.message };
        }
    };

    const logout = () => {
        localStorage.removeItem('mipa_token');
        localStorage.removeItem('mipa_user');
        setToken(null);
        setUser(null);
    };

    const isAuthenticated = () => {
        return !!token && !!user;
    };

    const getAuthHeaders = () => {
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const value = {
        user,
        token,
        login,
        logout,
        isAuthenticated,
        getAuthHeaders,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};